import React from "react";
import { Card, CardContent } from "../ui/card";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { SummaryTiles, Hint, IconAction, DueChip, PartiesChip } from "./PaymentBits";
import { tr, currentLang } from "./i18n";
import {
  fmtDate, fmtMonth, parseDay, today, daysBetween, days, money, moneyList, sumByCurrency, plural,
} from "./format";

// How payment works here: by the contract's invoice, bank transfer to the
// requisites of the legal entity on it. A card (Stripe) link exists only when
// the manager created one for that payment — the client never starts it.

const partTitle = (idx, total) => {
  if (total <= 1) return tr("Повна оплата");
  if (idx === 1) return tr("Передоплата");
  if (idx === total) return tr("Фінальний");
  return tr("Проміжний");
};
const partWhy = (idx, total) => {
  if (total <= 1) return tr("Повна оплата — уся вартість послуги одним платежем, без поділу на частини.");
  if (idx === 1) return tr("Передоплата — з неї ми запускаємо роботу.");
  if (idx === total) return tr("Фінальний платіж — після здачі робіт і вашого погодження.");
  return tr("Проміжний платіж — після проміжного етапу робіт.");
};

const TONE = {
  paid: { dot: "bg-green-500", text: "text-green-500", bar: "bg-green-500" },
  overdue: { dot: "bg-red-500", text: "text-red-500", bar: "bg-red-500" },
  invoiced: { dot: "bg-amber-500", text: "text-amber-500", bar: "bg-amber-500" },
  not_invoiced: { dot: "bg-zinc-600", text: "text-zinc-500", bar: "bg-zinc-800" },
};

// Short status of one part; the long form goes to the tooltip.
const partStatus = (p, penaltyRule) => {
  const now = today();
  const due = parseDay(p.invoice_due_date);
  const isLast = p.part_index === p.part_total;
  switch (p.state) {
    case "paid":
      return { short: fmtDate(p.paid_date) ? `${tr("Оплачено")} ${fmtDate(p.paid_date)}` : tr("Оплачено") };
    case "overdue": {
      const late = due ? daysBetween(due, now) : 0;
      let pen = null;
      if (p.penalty?.amount > 0) {
        pen = `${tr("пеня")} $${Number(p.penalty.amount).toLocaleString(currentLang() === "en" ? "en-GB" : "uk-UA", { maximumFractionDigits: 2 })}`;
      } else if (penaltyRule?.enabled) {
        const leftGrace = (penaltyRule.grace_period_days || 0) - late;
        if (leftGrace > 0) pen = `${tr("пеня з’явиться через")} ${days(leftGrace)}`;
      }
      return {
        short: `${tr("Прострочено")} ${days(late)}${pen ? ` · ${pen}` : ""}`,
        long: `${tr("Треба було оплатити до")} ${fmtDate(p.invoice_due_date)}.`,
      };
    }
    case "invoiced": {
      const left = due ? daysBetween(now, due) : null;
      return {
        short: due ? `${tr("До")} ${fmtDate(p.invoice_due_date)}${left > 0 ? ` · ${tr("ще")} ${days(left)}` : left === 0 ? ` · ${tr("сьогодні")}` : ""}` : tr("Рахунок надіслано"),
        long: p.invoice_sent_at ? `${tr("Рахунок надіслано")} ${fmtDate(p.invoice_sent_at)}.` : null,
      };
    }
    default:
      return {
        short: isLast && p.part_total > 1 ? tr("Після здачі робіт") : fmtMonth(p.month) ? `${tr("Орієнтовно")} ${fmtMonth(p.month)}` : tr("Пізніше"),
        long: tr("Рахунок ще не надіслано — зараз платити не потрібно."),
      };
  }
};

// One short line for the whole service.
const groupSummary = (parts) => {
  const overdue = parts.filter((x) => x.state === "overdue");
  const invoiced = parts.filter((x) => x.state === "invoiced");
  const paid = parts.filter((x) => x.state === "paid");
  const n = parts.length;
  if (overdue.length) return { tone: "overdue", text: `${tr("Прострочено")} ${moneyList(sumByCurrency(overdue))}` };
  if (invoiced.length) {
    const due = invoiced.map((x) => x.invoice_due_date).filter(Boolean).sort()[0];
    return { tone: "invoiced", text: `${tr("До оплати")} ${moneyList(sumByCurrency(invoiced))}${due ? ` ${tr("до")} ${fmtDate(due)}` : ""}` };
  }
  if (paid.length === n) return { tone: "paid", text: tr("Оплачено повністю") };
  const next = parts.find((x) => x.state === "not_invoiced");
  const when = next.part_index === n && n > 1 ? tr("після здачі") : fmtMonth(next.month) ? fmtMonth(next.month) : tr("пізніше");
  return { tone: "not_invoiced", text: `${paid.length ? `${tr("Сплачено")} ${paid.length} ${tr("з")} ${n} · ` : ""}${tr("далі")} ${money(next.amount, next.currency)} — ${when}` };
};

// The part whose invoice the collapsed row offers: what's due now, else the
// latest paid one, else the first.
const currentPart = (parts) =>
  parts.find((x) => x.state === "overdue") ||
  parts.find((x) => x.state === "invoiced") ||
  [...parts].reverse().find((x) => x.state === "paid") ||
  parts[0];

const PartActions = ({ p, onDownload }) => (
  <div className="flex items-center gap-1.5 justify-end">
    {p.stripe_url && <IconAction kind="card" href={p.stripe_url} label={tr("Оплатити карткою (посилання від менеджера)")} />}
    <IconAction
      kind="invoice"
      disabled={p.doc_ready === false}
      onClick={() => onDownload(p.id, "invoice")}
      label={
        p.doc_ready === false
          ? tr("Рахунок ще готує менеджер — реквізити для цього платежу не заповнені")
          : p.state === "not_invoiced" ? tr("Рахунок (ще не надіслано — можна завантажити заздалегідь)") : tr("Завантажити рахунок")
      }
    />
    {p.state === "paid" && <IconAction kind="act" onClick={() => onDownload(p.id, "act")} label={tr("Акт виконаних робіт")} />}
  </div>
);

// Numbers instead of sentences: which date matters for this part and how far
// it is (−N overdue / N left / ✓ paid / plan), the sentence goes to the tooltip.
const partDue = (p, penaltyRule) => {
  const st = partStatus(p, penaltyRule);
  const tip = [st.short, st.long].filter(Boolean).join(". ");
  if (p.state === "paid") return { kind: "done", date: p.paid_date, tip };
  if (p.state === "overdue") {
    const due = parseDay(p.invoice_due_date);
    const late = due ? daysBetween(due, today()) : 0;
    const g = penaltyRule?.enabled ? penaltyRule.grace_period_days || 0 : 0;
    const inGrace = g > 0 && late <= g && !(p.penalty?.amount > 0);
    return { kind: "overdue", date: p.invoice_due_date, tip, grace: inGrace ? { total: g, used: late } : null };
  }
  if (p.state === "invoiced") return { kind: "due", date: p.invoice_due_date, tip };
  const isLast = p.part_index === p.part_total && p.part_total > 1;
  return { kind: "planned", date: null, label: isLast ? tr("після здачі") : (fmtMonth(p.month) || tr("пізніше")), tip };
};

// One part = one line, laid out like the payment row on the project page:
// part · № · payer · status · amount · actions.
const PartRow = ({ p, onDownload, penaltyRule }) => {
  return (
    <div className="grid grid-cols-[8px_minmax(0,1fr)_auto] md:grid-cols-[8px_max-content_110px_minmax(0,1.2fr)_minmax(0,1fr)_120px_120px] items-center gap-x-4 gap-y-1 py-2">
      <span className={cn("w-2 h-2 rounded-full", TONE[p.state]?.dot)} />
      {/* Never truncated: the label is short, the column takes what it needs. */}
      <div className="flex items-center gap-1.5 whitespace-nowrap text-sm text-zinc-200">
        <span>
          {p.part_total > 1 ? `${p.part_index}/${p.part_total} · ` : ""}
          {partTitle(p.part_index, p.part_total)}
          <span className="text-zinc-500"> {p.part_total > 1 ? (p.part_percent ? `${p.part_percent}%` : "") : "100%"}</span>
        </span>
        <Hint text={partWhy(p.part_index, p.part_total)} />
      </div>
      <p className="hidden md:block text-xs text-zinc-500 font-mono truncate">{p.invoice_number ? `№ ${p.invoice_number}` : "—"}</p>
      <div className="hidden md:block min-w-0">
        <PartiesChip payer={p.bill_to} payee={p.issuer} />
      </div>
      <div className="col-start-2 row-start-2 md:col-start-auto md:row-start-auto min-w-0">
        <DueChip {...partDue(p, penaltyRule)} />
      </div>
      <p className="col-start-3 row-start-1 md:col-start-auto md:row-start-auto text-sm font-semibold text-zinc-100 tabular-nums whitespace-nowrap text-right">
        {money(p.amount, p.currency)}
      </p>
      <div className="col-start-3 row-start-2 md:col-start-auto md:row-start-auto">
        <PartActions p={p} onDownload={onDownload} />
      </div>
    </div>
  );
};

/** One service = one line; the parts open on click. */
export const ServiceRow = ({ parts, onDownload, penaltyRule }) => {
  const [open, setOpen] = React.useState(false);
  const first = parts[0];
  const n = parts.length;
  const sum = groupSummary(parts);
  const cur = currentPart(parts);
  // The part that matters now: overdue → invoiced → next planned → last paid.
  const nextPart = parts.find((x) => x.state === "overdue") || parts.find((x) => x.state === "invoiced")
    || parts.find((x) => x.state === "not_invoiced") || [...parts].reverse().find((x) => x.state === "paid") || parts[0];
  const cardPart = parts.find((x) => x.stripe_url && x.state !== "paid");
  return (
    <div className={cn("rounded-xl border transition-colors", open ? "border-zinc-700 bg-zinc-950/30" : "border-zinc-800 hover:border-zinc-700")}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen((v) => !v))}
        className="grid grid-cols-[16px_minmax(0,1fr)_auto] md:grid-cols-[16px_minmax(0,1.4fr)_minmax(0,1fr)_112px_150px_120px] items-center gap-x-3 px-3 sm:px-4 py-2.5 cursor-pointer select-none"
      >
        <ChevronRight className={cn("h-4 w-4 text-zinc-500 transition-transform", open && "rotate-90")} />
        <div className="min-w-0 flex items-center gap-2">
          <p className="text-sm font-semibold text-zinc-100 truncate" title={first.base_name || first.name}>{first.base_name || first.name}</p>
          <Hint text={n > 1
            ? `${tr("Вартість поділено на")} ${n} ${plural(n, ["платіж", "платежі", "платежів"])}: ${parts.map((x) => `${partTitle(x.part_index, x.part_total)}${x.part_percent ? ` ${x.part_percent}%` : ""}`).join(" → ")}.`
            : tr("Уся вартість послуги — одним платежем.")}>
            <span className="flex-shrink-0 rounded-md border border-zinc-700 px-1.5 py-px text-[11px] text-zinc-400 whitespace-nowrap">
              {n > 1 ? parts.map((x) => (x.part_percent ? `${x.part_percent}%` : "•")).join(" + ") : "100%"}
            </span>
          </Hint>
        </div>
        <div className="hidden md:block min-w-0" title={sum.text}>
          <DueChip {...partDue(nextPart, penaltyRule)} />
        </div>
        <div className="hidden md:flex gap-0.5" aria-hidden="true">
          {parts.map((x) => (
            <div key={x.id} className={cn("h-1.5 rounded-full", TONE[x.state]?.bar)}
              style={{ flex: Math.max(x.part_percent || 100 / n, 5) }} />
          ))}
        </div>
        <p className="text-sm font-bold text-zinc-50 tabular-nums whitespace-nowrap text-right">
          {moneyList(sumByCurrency(parts))}
        </p>
        <div className="hidden md:flex items-center gap-1.5 justify-end">
          {cardPart && <IconAction kind="card" href={cardPart.stripe_url} label={tr("Оплатити карткою (посилання від менеджера)")} />}
          <IconAction kind="invoice" onClick={() => onDownload(cur.id, "invoice")}
            disabled={cur.doc_ready === false}
            label={cur.doc_ready === false
              ? tr("Рахунок ще готує менеджер — реквізити для цього платежу не заповнені")
              : `${tr("Рахунок")}${n > 1 ? ` — ${tr("платіж")} ${cur.part_index} ${tr("з")} ${n}` : ""}${cur.issuer ? ` · ${tr("від")} ${cur.issuer}` : ""}`} />
        </div>
        <div className="md:hidden col-start-2"><DueChip {...partDue(nextPart, penaltyRule)} /></div>
      </div>
      {open && (
        <div className="px-3 sm:px-4 pb-1 pl-9 sm:pl-11 border-t border-zinc-800 divide-y divide-zinc-800/70">
          {parts.map((p) => (
            <PartRow key={p.id} p={p} onDownload={onDownload} penaltyRule={penaltyRule} />
          ))}
        </div>
      )}
    </div>
  );
};

/** Group payments into services (split parts together), urgent first. */
export const groupServices = (payments = []) => {
  const groups = [];
  const byKey = new Map();
  payments.forEach((p) => {
    const k = p.group_key || p.id;
    if (!byKey.has(k)) { byKey.set(k, []); groups.push(k); }
    byKey.get(k).push(p);
  });
  groups.forEach((k) => byKey.get(k).sort((a, b) => (a.part_index || 0) - (b.part_index || 0)));
  const urgency = (k) => {
    const st = byKey.get(k).map((x) => x.state);
    if (st.includes("overdue")) return 0;
    if (st.includes("invoiced")) return 1;
    if (st.includes("not_invoiced")) return 2;
    return 3;
  };
  return groups.sort((a, b) => urgency(a) - urgency(b)).map((k) => ({ key: k, parts: byKey.get(k), urgency: urgency(k) }));
};

/**
 * Payments of one project, full width. One line per service, parts on click,
 * explanations in tooltips.
 */
const ProjectPayments = ({ payments = [], penaltyRule, onDownload }) => {
  if (!payments.length) return null;

  const groups = [];
  const byKey = new Map();
  payments.forEach((p) => {
    const k = p.group_key || p.id;
    if (!byKey.has(k)) {
      byKey.set(k, []);
      groups.push(k);
    }
    byKey.get(k).push(p);
  });
  groups.forEach((k) => byKey.get(k).sort((a, b) => (a.part_index || 0) - (b.part_index || 0)));
  // Money due now first (overdue → invoiced), then planned; fully paid last.
  const urgency = (k) => {
    const st = byKey.get(k).map((x) => x.state);
    if (st.includes("overdue")) return 0;
    if (st.includes("invoiced")) return 1;
    return 2;
  };
  const isPaidGroup = (k) => byKey.get(k).every((x) => x.state === "paid");
  const openGroups = groups.filter((k) => !isPaidGroup(k)).sort((a, b) => urgency(a) - urgency(b));
  const paidGroups = groups.filter(isPaidGroup);

  const total = sumByCurrency(payments);
  const paid = sumByCurrency(payments.filter((p) => p.state === "paid"));
  const left = sumByCurrency(payments.filter((p) => p.state !== "paid"));
  const dueNow = sumByCurrency(payments.filter((p) => p.state === "invoiced" || p.state === "overdue"));

  const grace = penaltyRule?.grace_period_days || 0;
  const howItWorks = [
    tr("Оплата — переказом за реквізитами з рахунку, виставленого за договором (іконка рахунку в рядку)."),
    tr("Рахунок можна завантажити будь-коли; у ньому вказано, до якої дати його оплатити."),
    penaltyRule?.enabled
      ? `${tr("Якщо оплата затримується")}${grace > 0 ? ` ${tr("більше ніж на")} ${days(grace)}` : ""} ${tr("після цієї дати, нараховується пеня")} ${penaltyRule.rate_per_day}% ${tr("за день від суми")} ${penaltyRule.base === "project_total" ? tr("договору") : tr("рахунку")}.`
      : null,
    tr("Оплата карткою — лише якщо менеджер надіслав посилання (іконка картки)."),
    tr("Після оплати тут з’являється акт виконаних робіт."),
  ].filter(Boolean).join(" ");

  return (
    <section id="client-project-payments" data-testid="client-project-payments" className="space-y-3 scroll-mt-4">
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{tr("Оплата")}</p>
        <h3 className="text-lg font-bold text-zinc-50 font-['Manrope'] mt-0.5 flex items-center gap-2">
          {tr("Що і коли оплачувати")} <Hint text={howItWorks} />
        </h3>
      </div>
      <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
        <CardContent className="p-4 sm:p-5 space-y-3">
          <SummaryTiles
            tiles={[
              { label: tr("Вартість проєкту"), value: moneyList(total) || "—" },
              { label: tr("Уже сплачено"), value: moneyList(paid) || "0", tone: "paid" },
              { label: tr("Залишилось сплатити"), value: moneyList(left) || "0" },
              { label: tr("Оплатити зараз"), value: moneyList(dueNow) || tr("Нічого"), tone: dueNow.length ? "due" : "muted" },
            ]}
          />
          {openGroups.length > 0 && (
            <div className="space-y-2">
              {openGroups.map((k) => (
                <ServiceRow key={k} parts={byKey.get(k)} onDownload={onDownload} penaltyRule={penaltyRule} />
              ))}
            </div>
          )}
          {paidGroups.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500 pt-1">{tr("Уже оплачено")}</p>
              {paidGroups.map((k) => (
                <ServiceRow key={k} parts={byKey.get(k)} onDownload={onDownload} penaltyRule={penaltyRule} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default ProjectPayments;
